import {
  Flashbar,
  FlashbarProps,
  Alert,
  Button,
  SpaceBetween,
  Box,
} from '@cloudscape-design/components';
import { useEffect, useMemo } from 'react';
import { ApiRequestStatus } from 'src/types/ApiRequestStatus';
import {
  isVersionConflictError,
  getVersionConflictDetails,
} from 'src/utils/generalUtils';

interface ApiStatusFlashBarProps {
  apiStatus: ApiRequestStatus[];
  clearMessages: () => void;
  autoDismissDelay?: number;
  onRefresh?: () => void;
}

const DEFAULT_AUTO_DISMISS_DELAY = 5000; // 5 seconds

const ApiStatusFlashBar: React.FC<ApiStatusFlashBarProps> = ({
  apiStatus,
  clearMessages,
  autoDismissDelay = DEFAULT_AUTO_DISMISS_DELAY,
  onRefresh,
}) => {
  // Auto-dismiss success messages
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    timeoutId = setTimeout(() => {
      clearMessages();
    }, autoDismissDelay);

    // Cleanup timeout on component unmount or when message changes
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [apiStatus]);

  const messageList = useMemo<FlashbarProps.MessageDefinition[]>(() => {
    if (!apiStatus.length) return [];

    return apiStatus
      .map((status, index) => {
        const { errorMessage, successMessage, headerMessage, errorHtml, infoMessage } =
          status;

        if (!errorMessage && !successMessage && !infoMessage) return null;

        // Handle info messages (retry/pagination notifications)
        if (infoMessage && !errorMessage && !successMessage) {
          return {
            header: headerMessage,
            type: 'info' as const,
            content: infoMessage,
            dismissible: true,
            onDismiss: clearMessages,
            id: `info-${index}`,
          };
        }

        // Handle version conflict errors with inline Alert
        if (errorMessage && isVersionConflictError({ message: errorMessage })) {
          let errorData;
          try {
            errorData = JSON.parse(errorMessage);
          } catch {
            errorData = { message: errorMessage };
          }

          const details = getVersionConflictDetails(errorData);
          return {
            header: 'Version Conflict Detected',
            type: 'error' as const,
            content: (
              <SpaceBetween size="s">
                <Box>
                  The question has been modified by{' '}
                  <strong>{details.lastModifiedBy}</strong> while you were editing. Your
                  changes cannot be saved to prevent data loss.
                </Box>
                <Box>
                  <strong>Details:</strong>
                  <ul>
                    <li>Your version: {details.expectedVersion}</li>
                    <li>Current version: {details.actualVersion}</li>
                    <li>Last modified by: {details.lastModifiedBy}</li>
                  </ul>
                </Box>
                {onRefresh && (
                  <Button variant="primary" onClick={onRefresh}>
                    Refresh and Try Again
                  </Button>
                )}
              </SpaceBetween>
            ),
            dismissible: true,
            onDismiss: clearMessages,
            id: `version-conflict-${index}`,
          };
        }

        return {
          header: headerMessage,
          type: errorMessage || errorHtml ? 'error' : 'success',
          content: errorHtml || errorMessage || successMessage || '',
          dismissible: true,
          onDismiss: clearMessages,
          id: `message-${index}`,
        };
      })
      .filter(Boolean) as FlashbarProps.MessageDefinition[];
  }, [apiStatus, clearMessages, onRefresh]);

  if (!messageList.length) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        width: '80%',
        maxWidth: '800px',
      }}
    >
      <Flashbar stackItems items={messageList} />
    </div>
  );
};

export default ApiStatusFlashBar;
